import React, { createContext, useContext, useCallback, useState } from 'react'
import Screening, { buildScreening } from '../classes/Screening'
import { getCache, setCache } from '@/utils/cache'
import { useForceUpdate } from '@react-spring/shared'
import { useHistory } from 'react-router-dom'
import { screeningPages } from '../constants'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useAttendanceContext } from '@/modules/Attendance/contexts/AttendanceContex'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import Attendance from '@/modules/Attendance/classes/Attendance'
import { redirectToCheckout } from '@/modules/Checkout/utils/redirectToCheckout'

type updateScreeningFn = (screening: Screening) => Screening

interface IScreeningContext {
  screeningData: Screening
  setScreeningData(call: updateScreeningFn): void
  nextStep(): void
  lastStep(): void
  cleanScreening(): void
}

const ScreeningContextData = createContext<IScreeningContext>(
  {} as IScreeningContext,
)

const ScreeningContext: React.FC = ({ children }) => {
  const history = useHistory()
  const { setAttendance } = useAttendanceContext()
  const { specialities } = useMedicalSpecialitiesContext()

  const [screeningData, setScreeningData] = useState<Screening>(() => {
    const savedData = getCache<Screening>('screening')
    return buildScreening(savedData || {})
  })

  const forceUpdate = useForceUpdate()

  const removeSlashFromGetters = (screening: Screening) => {
    const keys = Object.keys(screening)
    const values = Object.values(screening)
    const parsedScreening = keys?.map((key, index) => {
      return { [key.replace(/_/g, '')]: values[index] }
    })
    return parsedScreening.reduce((list, current) => {
      return { ...list, ...current }
    })
  }

  const updateScreening = useCallback(
    (call: updateScreeningFn) => {
      const newScreening = call(screeningData)
      setCache('screening', removeSlashFromGetters(newScreening))

      setScreeningData(newScreening)
      forceUpdate()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [forceUpdate, screeningData],
  )
  const { clinic } = useAuthContext()

  const nextStep = useCallback(() => {
    const currentStep = screeningData.currentStep
    const nextStepIndex = currentStep === undefined ? 0 : currentStep + 1
    const routes = screeningData?.steps
    if (routes === undefined) history.push('triagem')
    const nextPage = routes?.[nextStepIndex]
    const nextRoute = screeningPages[nextPage]?.route

    if (!nextRoute) {
      const Speciality = specialities?.filter(
        s => s.id === screeningData?.professionalType,
      )?.[0]

      setAttendance(() => {
        const att = new Attendance(Speciality)
        return att
      })
      redirectToCheckout({
        attendanceType: 'immediate',
        push: history.push,
        hasPayment: clinic?.flag?.PermiteConsultaParticular,
        hasMultipleCards: clinic?.flag?.HabilitarMultiplosCartoesConvenio,
      })
    } else {
      updateScreening(screening => {
        screening.currentStep = nextStepIndex
        return screening
      })
      history.push(nextRoute)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, screeningData?.currentStep, screeningData?.steps])

  const lastStep = useCallback(() => {
    const routes = screeningData?.steps
    const nextPage = routes[routes?.length - 1]
    const nextRoute = screeningPages[nextPage]?.route
    updateScreening(screening => {
      screening.currentStep = routes?.length - 1
      return screening
    })
    history.push(nextRoute)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, screeningData?.steps])

  const cleanScreening = () => {
    setScreeningData(screening => {
      screening.token = null
      screening.clinic = null
      screening.steps = []
      screening.currentStep = 0
      screening.patient = null
      screening.hasSymptons = null
      screening.hasDiseaseOrMedicines = null
      screening.symptons = {
        symptons: [],
        hardSymptons: [],
        intensity: null,
      }
      screening.medicines = []
      screening.attachments = []
      screening.diseases = []
      screening.allergies = {
        isAllergicToIodine: null,
        isAllergicToMedicines: null,
        isAllergicToSeafood: null,
        medicines: [],
      }
      screening.professional = null
      screening.professionalType = null
      screening.attachments = []
      screening.currentStep = undefined
      screening.startedAt = null
      screening.finishedAt = null
      return screening
    })
    forceUpdate()
  }

  return (
    <ScreeningContextData.Provider
      value={{
        screeningData,
        setScreeningData: updateScreening,
        cleanScreening,
        nextStep,
        lastStep,
      }}
    >
      {children}
    </ScreeningContextData.Provider>
  )
}

export function useScreeningData(): IScreeningContext {
  const context = useContext(ScreeningContextData)

  if (!context) {
    throw new Error('useScreeningData must be used within an ScreeningProvider')
  }

  return context
}

export default ScreeningContext
